import { Box, Center, Flex, Stack, Text, useToast } from "@chakra-ui/react";
import React, { type FormEvent } from "react";
import { theme } from "src/styles/theme";
import { IoTrashOutline } from "react-icons/io5";
import InputText from "@components/Sales/Inputs/InputText";
import InputDate from "@components/Sales/Inputs/InputDate";
import Button from "@components/common/Button";
import { BiPlus } from "react-icons/bi";
import { MdOutlineSave } from "react-icons/md";
import InputSelectWithService from "@components/Sales/Inputs/InputSelectWithService";
import quotesService from "@services/Quotes/QuotesService";
import { OportunityContext } from "@context/oportunityContex";
import Spinner from "@components/common/Spinner";
import MessageToast from "@components/Sales/MessageToast";
import { format, parseISO } from "date-fns";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { useOnlyMessageToast } from "@hook/useMessageToast";
import { IconInfo } from "src/assets/iconInfo";

type Props = {
  hasChanges: boolean;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setHasDeleteButDontSaveMensalPs: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  data: any;
  bonusAdimplencia: number;
  discount: string;
  successCreateMensalPs: boolean;
  setDiscount: React.Dispatch<React.SetStateAction<string>>;
  setExistMensalPs: React.Dispatch<React.SetStateAction<boolean>>;
  setEmptyPortion: React.Dispatch<React.SetStateAction<boolean>>;
  disabledIncome: boolean;
  disabledStatusApprove: boolean | undefined;
  disableEdit: boolean | undefined;
  onSalvarParcelamento: () => void;
  onAdicionarParcela: () => void;
  dataHabitese?: string;
  statusEmpreendimento?: string;
  setDataProposalAnalysis: React.Dispatch<React.SetStateAction<any[]>>;
};

type BoxItem = {
  id: number;
  idParcelamentoSalesforce: string;
  serie: string;
  quantidade: string;
  valor: number;
  prosoluto: number;
  dataPagamento: string;
  deletado: boolean;
  isDisabled?: boolean;
  tipoFluxo: string;
  valorRaw?: number;
  valorTotalCondicaoBackend?: number;
};

type LocalDraft = {
  boxes: BoxItem[];
  savedAt: string;
};

const titleHeaderTableNoProsoluto = [
  { id: "#", name: "#" },
  { id: "Série", name: "Série" },
  { id: "Quantidade", name: "Quantidade" },
  { id: "Valor", name: "Valor" },
  { id: "Data de pagamento", name: "Data de pagamento" },
  { id: "Percentual", name: "Percentual" },
  { id: "Valor total da condição", name: "Valor total da condição" },
];
const titleHeaderTable = [
  { id: "#", name: "#" },
  { id: "Série", name: "Série" },
  { id: "Quantidade", name: "Quantidade" },
  { id: "Valor", name: "Valor" },
  { id: "Data de pagamento", name: "Data de pagamento" },
  { id: "Percentual", name: "Percentual" },
  { id: "% pro soluto", name: "% pró soluto" },
  { id: "Valor total da condição", name: "Valor total da condição" },
];

const initialBox: BoxItem = {
  id: 1,
  idParcelamentoSalesforce: "",
  serie: "",
  quantidade: "1",
  valor: 0,
  prosoluto: 0,
  dataPagamento: "",
  deletado: false,
  isDisabled: false,
  tipoFluxo: "",
};

// Lista de séries que devem ter data automática
const seriesWithAutoDate = [
  "Financiamento",
  "FGTS",
  "B. Adimplência",
  "Bônus Adimplência",
  "Subsídio",
  "Subsidio",
];

const TablePortion = ({
  data,
  // discount,
  // setDiscount,
  hasChanges,
  setHasChanges,
  successCreateMensalPs,
  setHasDeleteButDontSaveMensalPs,
  setExistMensalPs,
  setEmptyPortion,
  disabledIncome,
  disableEdit,
  onAdicionarParcela,
  onSalvarParcelamento,
  disabledStatusApprove,
  bonusAdimplencia,
  dataHabitese,
  statusEmpreendimento,
  setDataProposalAnalysis,
}: Props) => {
  const toast = useToast();
  const {
    accessQuote,
    faseOp,
    paymentCondition,
    isSendToApproval,
    discount,
    setDiscount,
  } = React.useContext(OportunityContext);
  const displayMessageError = useMessageErrorToast();
  const displayOnlyMessage = useOnlyMessageToast();
  const [boxes, setBoxes] = React.useState<BoxItem[]>([]);
  const [formattedDiscount, setFormattedDiscount] = React.useState(0);
  const [somaPercentuais, setSomaPercentuais] = React.useState(0);

  const [error, setError] = React.useState(false);
  const [messageError, setMessageError] = React.useState("");

  const [idSerie, setIdSerie] = React.useState("");
  const [loadingSerie, setLoadingSerie] = React.useState(false);
  const [dataSerie, setDataSerie] = React.useState([]);
  const [loadingInstallment, setLoadingInstallment] = React.useState(false);
  const [loadingCreateInstallment, setLoadingCreateInstallment] =
    React.useState(false);
  const [isEscalonado, setIsEscalonado] = React.useState(false);

  // --- Persistência local (rascunho de parcelas não salvas) ---
  const [showUnsavedMessage, setShowUnsavedMessage] = React.useState(false);
  const [hasLocalDraft, setHasLocalDraft] = React.useState(false);
  const [draftSavedAt, setDraftSavedAt] = React.useState<string | null>(null);
  const isRestoringDraft = React.useRef(false);
  // Marca se o usuário editou algo (adicionou/alterou/removeu parcela)
  // enquanto uma busca à API (handleGetInstallment) estava em andamento.
  // Evita que a resposta da API, ao chegar depois, sobrescreva/apague
  // uma edição local que o usuário acabou de fazer.
  const hasLocalEditSinceFetchRef = React.useRef(false);
  // Evita que o efeito de limpeza do rascunho (mais abaixo) apague o
  // rascunho do localStorage logo após ele ter sido restaurado na
  // montagem — nesse instante o state `boxes` ainda não refletiu o
  // rascunho (setBoxes é assíncrono), então aquele efeito "vê" boxes
  // vazio e, sem essa proteção, chamaria clearLocalDraft() por engano,
  // fazendo o rascunho sumir num próximo reload da página.
  const skipClearOnRestoreRef = React.useRef(false);
  const hasUserEditedRef = React.useRef(false);

  const markLocalEdit = React.useCallback(() => {
    hasLocalEditSinceFetchRef.current = true;
    hasUserEditedRef.current = true;
  }, []);

  const getLocalStorageKey = React.useCallback(
    () => `parcelas_draft_${accessQuote.idCotacaoSalesforce}`,
    [accessQuote.idCotacaoSalesforce],
  );

  const saveDraftToLocalStorage = React.useCallback(
    (boxesToSave: BoxItem[]) => {
      const draft: LocalDraft = {
        boxes: boxesToSave,
        savedAt: new Date().toISOString(),
      };

      try {
        localStorage.setItem(getLocalStorageKey(), JSON.stringify(draft));
        setHasLocalDraft(true);
        setDraftSavedAt(draft.savedAt);
      } catch (error) {
        console.log("ErrorSaveDraftToLocalStorage", error);
      }
    },
    [getLocalStorageKey],
  );

  const clearLocalDraft = React.useCallback(() => {
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (error) {
      console.log("ErrorClearLocalDraft", error);
    }
    setHasLocalDraft(false);
    setDraftSavedAt(null);
    hasUserEditedRef.current = false; // reinicia: não há mais edição pendente
  }, [getLocalStorageKey]);

  const handleDiscardLocalDraft = () => {
    clearLocalDraft();
    setHasChanges(false);
    setShowUnsavedMessage(false);
    handleGetInstallment();
  };

  const valueNumberFormat = (valor: number | string) => {
    const newValor = valor
      ?.toString()
      .replace("R$", "")
      .trim()
      .replace(/\./g, "")
      .replace(",", ".");
    const valorNumerico = Number.parseFloat(newValor);
    return valorNumerico;
  };

  // Usa o valor unitário com precisão total vindo do backend, se ainda
  // disponível (parcela não editada). Caso contrário, usa o valor exibido.
  const getBoxValorNumerico = (box: BoxItem) => {
    if (typeof box.valorRaw === "number" && !Number.isNaN(box.valorRaw)) {
      return box.valorRaw;
    }
    return valueNumberFormat(box.valor);
  };

  // Usa o total da condição já calculado pelo backend, se ainda disponível
  // (parcela não editada). Caso contrário, recalcula quantidade × valor.
  const getBoxTotalCondicao = (box: BoxItem) => {
    if (
      typeof box.valorTotalCondicaoBackend === "number" &&
      !Number.isNaN(box.valorTotalCondicaoBackend)
    ) {
      return box.valorTotalCondicaoBackend;
    }
    return getBoxValorNumerico(box) * Number(box.quantidade);
  };

  const valueTotalCondition = (box: BoxItem) => {
    return formatMoney(roundMoney(getBoxTotalCondicao(box)));
  };

  const valorTotalComDesconto = data?.valorTotalUnidade - formattedDiscount;

  const sumValoresTotalRaw = () => {
    return boxes
      .filter((box) => !box.deletado)
      .reduce((acc, box) => acc + roundMoney(getBoxTotalCondicao(box)), 0);
  };

  const roundMoney = (valor: number) =>
    Math.round((valor + Number.EPSILON) * 100) / 100;

  const ROUNDING_TOLERANCE_MONEY = 0.3; // R$ 0,10
  const ROUNDING_TOLERANCE_PERCENT = 0.1; // 0,10 p.p.

  const saldoADistribuir = (() => {
    const diff = roundMoney(valorTotalComDesconto - sumValoresTotalRaw());
    return Math.abs(diff) <= ROUNDING_TOLERANCE_MONEY ? 0 : diff;
  })();

  const calcularPercentualParcela = (box: BoxItem) => {
    if (valorTotalComDesconto <= 0) return "0%";

    const total = getBoxTotalCondicao(box);
    const percentual = (total / valorTotalComDesconto) * 100;

    return `${percentual.toFixed(2)}%`;
  };

  const handleAddBox = () => {
    const newId = boxes.reduce((maxId, box) => Math.max(box.id, maxId), 0) + 1;
    const newBox = { ...initialBox, id: newId, deletado: false };

    // Atualizar o estado local sem causar recarregamento
    setBoxes((prevBoxes) => [...prevBoxes, newBox]);
    setHasChanges(true);
    markLocalEdit();
    onAdicionarParcela();
  };

  const handleDisabledButtonAddBox = () => {
    if (
      !canAddMoreBoxes ||
      faseOp ||
      hasMensalPSPending() ||
      disableEdit ||
      disabledStatusApprove ||
      (disabledIncome && isAssociative)
    )
      return;
    else {
      handleAddBox();
    }
  };

  const handleRemoveBox = (id: number, serie: string) => {
    const boxToRemove = boxes.find((box) => box.id === id);

    const isPartiallyEmptyBox =
      boxToRemove &&
      (boxToRemove.serie === "" ||
        boxToRemove.quantidade === "" ||
        boxToRemove.valor === 0 ||
        boxToRemove.dataPagamento === "");

    if (isPartiallyEmptyBox) {
      const remainingBoxes = boxes.filter((box) => box.id !== id);

      setBoxes(remainingBoxes);

      const hasActiveBoxes = remainingBoxes.some((box) => !box.deletado);

      if (!hasActiveBoxes) {
        clearLocalDraft();
        setHasChanges(false);
        setShowUnsavedMessage(false);
      } else {
        markLocalEdit();
        setHasChanges(true);
      }
    } else {
      // Uma parcela só precisa ser marcada como "deletado: true" (soft
      // delete) se ela já existir no backend — é isso que avisa o backend
      // para excluí-la de fato. Uma parcela que o usuário acabou de criar
      // e ainda não salvou (idParcelamentoSalesforce vazio) não existe no
      // backend, então basta removê-la do array: some da tela e nunca
      // chega a entrar no payload de handleCreatePortion.
      let updatedBoxes: BoxItem[];

      if (serie !== "Mensal PS") {
        const isBackedByServer = !!boxToRemove?.idParcelamentoSalesforce;

        updatedBoxes = isBackedByServer
          ? boxes.map((box) =>
              box.id === id ? { ...box, deletado: true } : box,
            )
          : boxes.filter((box) => box.id !== id);
      } else {
        // Pode haver mais de uma linha "Mensal PS": cada uma segue a
        // mesma regra individualmente (existe no backend -> soft delete;
        // é só local -> remove de vez).
        updatedBoxes = boxes.reduce<BoxItem[]>((acc, box) => {
          if (box.serie !== "Mensal PS") {
            acc.push(box);
            return acc;
          }

          if (box.idParcelamentoSalesforce) {
            acc.push({ ...box, deletado: true });
          }
          // sem idParcelamentoSalesforce: não entra no array (removida)

          return acc;
        }, []);
      }

      setBoxes(updatedBoxes);

      const hasActiveBoxes = updatedBoxes.some((box) => !box.deletado);

      // Mesmo sem parcelas ativas, se alguma parcela excluída já existia
      // no backend (idParcelamentoSalesforce preenchido), a exclusão em si
      // é uma alteração pendente que precisa ser salva.
      const hasDeletedBackendInstallments = updatedBoxes.some(
        (box) => box.deletado && !!box.idParcelamentoSalesforce,
      );

      if (!hasActiveBoxes && !hasDeletedBackendInstallments) {
        clearLocalDraft();
        setHasChanges(false);
        setShowUnsavedMessage(false);
      } else {
        setHasChanges(true);
        markLocalEdit();
      }
    }
  };

  // Função auxiliar para obter a data automática com base no status do empreendimento
  const getAutoDateBasedOnStatus = () => {
    // Se o empreendimento estiver em obras e tiver data de habite-se, use-a
    if (
      statusEmpreendimento?.toLocaleLowerCase() === "em obras" &&
      dataHabitese
    ) {
      return dataHabitese;
    }
    // Se o empreendimento estiver pronto, use a data atual + 30 dias
    else if (statusEmpreendimento?.toLocaleLowerCase() === "pronto") {
      const today = new Date();
      today.setDate(today.getDate() + 30);
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    // Para qualquer outro caso, use a data atual
    else return "";
  };

  const handleInputChange = (
    id: number,
    field: keyof BoxItem,
    value: string | number,
  ) => {
    setHasChanges(true);
    markLocalEdit();

    if (field === "quantidade") {
      const numericValue = Number(value);

      if (numericValue > 0 && !isNaN(numericValue)) {
        setBoxes(
          boxes.map((box) =>
            box.id === id
              ? {
                  ...box,
                  [field]: numericValue?.toString(),
                  valorTotalCondicaoBackend: undefined, // total precisa ser recalculado
                }
              : box,
          ),
        );
      } else {
        setBoxes(
          boxes.map((box) =>
            box.id === id
              ? { ...box, [field]: "", valorTotalCondicaoBackend: undefined }
              : box,
          ),
        );
      }
    } else if (field === "valor") {
      setBoxes(
        boxes.map((box) =>
          box.id === id
            ? {
                ...box,
                valor: value as number, // mantém o comportamento existente (string formatada é armazenada aqui, apesar do tipo)
                valorRaw: undefined, // usuário digitou um novo valor
                valorTotalCondicaoBackend: undefined,
              }
            : box,
        ),
      );
    } else if (field === "serie") {
      // Garantir que o valor da série seja sempre uma string
      const serieValue = String(value);

      // Encontrar o box atual para verificar a série anterior
      const currentBox = boxes.find((box) => box.id === id);

      // Verificar se a série anterior era uma das séries especiais
      const wasSpecialSeries =
        currentBox && seriesWithAutoDate.includes(currentBox.serie);

      // Verificar se a nova série é uma das séries especiais
      const isSpecialSeries = seriesWithAutoDate.includes(serieValue);

      // Criar uma cópia profunda do array de boxes para evitar problemas de referência
      const newBoxes = [...boxes];

      // Encontrar o índice do box que está sendo atualizado
      const boxIndex = newBoxes.findIndex((box) => box.id === id);

      if (boxIndex !== -1) {
        // Atualizar a série
        newBoxes[boxIndex] = {
          ...newBoxes[boxIndex],
          serie: serieValue,
        };

        // Se a nova série for especial, definir a data automaticamente
        if (isSpecialSeries) {
          const autoDate = getAutoDateBasedOnStatus();
          newBoxes[boxIndex] = {
            ...newBoxes[boxIndex],
            dataPagamento: autoDate,
          };
        }
        // Se a série anterior era especial e a nova não é, limpar a data
        else if (wasSpecialSeries) {
          newBoxes[boxIndex] = {
            ...newBoxes[boxIndex],
            dataPagamento: "",
          };
        }
        // Caso contrário, manter a data atual

        // Atualizar o estado com o novo array
        setBoxes(newBoxes);
      }
    } else {
      setBoxes(
        boxes.map((box) => (box.id === id ? { ...box, [field]: value } : box)),
      );
    }
  };

  const handleChangeMoneyDiscount = (
    event: React.ChangeEvent<HTMLInputElement> | FormEvent<HTMLInputElement>,
    serie: string,
    bonusAdimplencia: number,
  ) => {
    let inputValue = (event.target as HTMLInputElement).value;

    inputValue = inputValue.replace(/\D/g, "");

    const numericValue = Number(inputValue) / 100;

    const formattedValue = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue);
    return { numericValue, formattedValue };
  };

  const formatMoney = (valor: number) => {
    const newValue = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
    return newValue;
  };

  const canAddMoreBoxes = boxes
    .filter((box) => !box.deletado)
    .every(
      (box) => box.serie && box.quantidade && box.valor && box.dataPagamento,
    );

  const handleAdjustWidth = (id: string) => {
    if (id === "#") return "25%";
    if (id === "Ação") return "75%";
    if (id === "Percentual") return "40%";
    return undefined;
  };

  const handleValueDiscount = (valor: number, discount: number) => {
    const newTotal = valor - discount;

    return formatMoney(newTotal);
  };

  const getDateToDay = () => {
    const today = new Date();
    today.setDate(today.getDate());
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleGetTypeInstallment = async () => {
    setLoadingSerie(true);
    try {
      const response = await quotesService.getTypeInstallment({
        IdCondicaoDePagamentoMatriz: data?.condicaoPagamentoMatrizSelecionada,
      });

      if (response.status >= 400 && response?.status !== 401) {
        setLoadingSerie(false);
        setDataSerie([]);
        console.log("status 400", response);
        return;
      }
      if (response.status === 401) {
        setLoadingSerie(false);
        setDataSerie([]);
        return;
      }

      const dataArray: any = Object.values(response);

      setDataSerie(dataArray);
      setLoadingSerie(false);
    } catch (error) {
      setLoadingSerie(false);
      console.log("ErrorHandleGetTypeInstallment", error);
    }
  };

  const handleGetInstallment = async () => {
    // Reseta o marcador de edição local: a partir daqui, qualquer alteração
    // do usuário (adicionar/editar/remover parcela) feita ANTES desta busca
    // terminar deve impedir que o resultado da API sobrescreva o estado.
    hasLocalEditSinceFetchRef.current = false;
    setLoadingInstallment(true);
    try {
      const response = await quotesService.getInstallment({
        IdCotacaoSalesforce: accessQuote.idCotacaoSalesforce,
      });

      if (response.status >= 400 && response?.status !== 401) {
        setLoadingInstallment(false);
        console.log("status 400", response);
        return;
      }
      if (response.status === 401) {
        setLoadingInstallment(false);
        return;
      }

      // Se o usuário adicionou/editou/removeu uma parcela enquanto essa
      // busca estava em andamento, não sobrescreve o que ele já fez —
      // caso contrário a parcela recém-criada "some" da tela.
      if (hasLocalEditSinceFetchRef.current) {
        setLoadingInstallment(false);
        return;
      }

      const formatDate = (date: string) => {
        const [value] = date?.split("T");
        return value;
      };

      const newResponse = response ? response : [];

      let counter = 1;
      const formattedData = newResponse?.map((parcela: any) => ({
        id: counter++,
        idParcelamentoSalesforce: parcela?.idParcelamentoSalesforce,
        serie: parcela?.serie,
        quantidade: parcela?.quantidade?.toString(),
        valor: formatMoney(parcela?.valor),
        valorRaw: parcela?.valor,
        valorTotalCondicaoBackend: parcela?.valorTotalCondicao,
        dataPagamento: formatDate(parcela?.dataDePagamento),
        prosoluto: parcela?.percentualProSoluto,
        tipoFluxo: parcela?.tipoFluxo,
        deletado: false,
        isDisabled: true,
      }));

      // setDataProposalAnalysis(response?.mensagensProposta ? response?.mensagensProposta : [])
      setBoxes(formattedData);
      setLoadingInstallment(false);
    } catch (error) {
      setLoadingInstallment(false);
      console.log("ErrorHandleGetInstallment", error);
    }
  };

  const handleCreatePortion = async () => {
    setError(false);
    setLoadingCreateInstallment(true);

    const isDataValida = (data: string): boolean => {
      const dataObjeto = new Date(data);

      const anoAtual = new Date().getFullYear();
      const anoData = dataObjeto.getFullYear();

      return anoData >= anoAtual;
    };

    const todasDatasValidas = boxes.every((box) =>
      isDataValida(box.dataPagamento),
    );

    if (!todasDatasValidas) {
      setLoadingCreateInstallment(false);
      displayMessageError({
        message: "Uma ou mais datas de pagamento são inválidas.",
      });
      return;
    }

    try {
      const portions = boxes.map((box) => ({
        idCotacaoSalesforce: data?.idCotacaoSalesforce,
        idParcelamentoSalesforce: box.idParcelamentoSalesforce,
        nome: accessQuote.nomeDaCotacao,
        quantidade: Number(box.quantidade),
        DataDePagamentoFormatada: format(
          parseISO(box.dataPagamento),
          "dd/MM/yyyy",
        ),
        serie: box.serie,
        valor: valueNumberFormat(box.valor),
        deletado: box.deletado,
        tipoFluxo: box.tipoFluxo,
        percentualProSoluto: Number(box.prosoluto),
      }));

      const response = await quotesService.updatePortion({
        portions,
      });

      if (response.status >= 400 && response?.status !== 401) {
        setLoadingCreateInstallment(false);
        setError(true);
        displayMessageError({ message: response?.data });
        // setMessageError(response?.data);
        console.log("status 400", response);
        // Mantém o rascunho local salvo: o erro pode ter recarregado a página
        // antes, e o usuário não pode perder o que já preencheu.
        // handleGetInstallment();
        return;
      }
      if (response.status === 401) {
        setLoadingCreateInstallment(false);
        return;
      }

      onSalvarParcelamento();
      displayOnlyMessage("Parcelamento salvo com sucesso!");
      clearLocalDraft();
      setHasChanges(false);
      setShowUnsavedMessage(false);
      handleGetInstallment();
      setLoadingCreateInstallment(false);
    } catch (error) {
      setLoadingCreateInstallment(false);
      console.log("ErrorHandleCreatePortion", error);
    }
  };
  const boxesEmpty = () => {
    const box = boxes.filter((box) => !box.deletado).length > 0;

    setEmptyPortion(box);

    return box;
  };

  const handleExistMensaPS = () => {
    const existeMensalPs = boxes.some(
      (box) => box.serie === "Mensal PS" && !box.deletado,
    );

    const notDeleted = boxes.some((box) => box.serie === "Mensal PS");
    setExistMensalPs(notDeleted);
    setHasDeleteButDontSaveMensalPs(!existeMensalPs);

    return existeMensalPs;
  };

  const hasMensalPSPending = () => {
    return boxes.some(
      (box) =>
        box.serie === "Mensal PS" &&
        (!box.deletado || (box.deletado && !!box.idParcelamentoSalesforce)),
    );
  };

  const isAssociative =
    paymentCondition?.toLowerCase()?.includes("associativ") ||
    paymentCondition?.toLowerCase()?.includes("sfh");

  const sumValoresTotal = () => {
    const somaValores = sumValoresTotalRaw();
    const diff = valorTotalComDesconto - somaValores;

    // Se a diferença estiver dentro da margem de tolerância, exibe o próprio
    // valor total com desconto — mantendo consistência com o "Saldo à
    // distribuir", que nesse caso também é exibido como R$ 0,00.
    const somaAjustada =
      Math.abs(diff) <= ROUNDING_TOLERANCE_MONEY
        ? valorTotalComDesconto
        : somaValores;

    return formatMoney(roundMoney(somaAjustada));
  };

  const isSalvarParcelamentoDisabled =
    handleExistMensaPS() ||
    isSendToApproval ||
    faseOp ||
    !canAddMoreBoxes ||
    boxes.length === 0 ||
    loadingCreateInstallment ||
    valueNumberFormat(discount) > valueNumberFormat(data?.valorTotalUnidade) ||
    disableEdit ||
    disabledStatusApprove;

  const isAddPortionDisabled =
    !canAddMoreBoxes ||
    faseOp ||
    hasMensalPSPending() ||
    disableEdit ||
    disabledStatusApprove ||
    (disabledIncome && isAssociative);

  React.useEffect(() => {
    if (!showUnsavedMessage && hasChanges && !isSalvarParcelamentoDisabled) {
      setShowUnsavedMessage(true);
    }
  }, [hasChanges, isSalvarParcelamentoDisabled, showUnsavedMessage]);

  const isBoxEmpty = React.useCallback((box: BoxItem) => {
    return (
      !box.serie ||
      !box.quantidade ||
      !box.dataPagamento ||
      valueNumberFormat(box.valor) === 0 ||
      Number.isNaN(valueNumberFormat(box.valor))
    );
  }, []);

  React.useEffect(() => {
    const calcularSomaPercentuais = () => {
      const somaPercentuais = boxes
        .filter((box) => !box.deletado)
        .reduce((acc, box) => {
          const total = valueNumberFormat(box.valor) * Number(box.quantidade);
          const percentual =
            valorTotalComDesconto > 0
              ? (total / valorTotalComDesconto) * 100
              : 0;
          return acc + percentual;
        }, 0);

      return somaPercentuais;
    };

    const soma = calcularSomaPercentuais();

    // Se a soma estiver dentro da margem de tolerância em relação a 100%,
    // exibe exatamente 100% em vez de um valor tipo 99.98% ou 100.03%
    // causado por arredondamento acumulado das parcelas.
    const somaAjustada =
      Math.abs(soma - 100) <= ROUNDING_TOLERANCE_PERCENT ? 100 : soma;

    setSomaPercentuais(Number(somaAjustada.toFixed(2)));
  }, [boxes, valorTotalComDesconto]);

  // Ao montar (ou trocar de cotação), tenta restaurar um rascunho local
  // antes de buscar os dados salvos na API. Isso evita perder parcelas
  // preenchidas caso a página tenha recarregado por causa de um erro.
  React.useEffect(() => {
    let restoredFromDraft = false;

    try {
      const draftRaw = localStorage.getItem(getLocalStorageKey());

      if (draftRaw) {
        const draft: LocalDraft = JSON.parse(draftRaw);

        if (draft?.boxes?.length) {
          isRestoringDraft.current = true;
          skipClearOnRestoreRef.current = true;
          setBoxes(draft.boxes);
          setHasChanges(true);
          setHasLocalDraft(true);
          setDraftSavedAt(draft.savedAt);
          setLoadingInstallment(false);
          restoredFromDraft = true;
        }
      }
    } catch (error) {
      console.log("ErrorRestoreLocalDraft", error);
      localStorage.removeItem(getLocalStorageKey());
    }

    if (
      !restoredFromDraft &&
      (successCreateMensalPs || accessQuote.idCotacaoSalesforce)
    ) {
      handleGetInstallment();
    }
  }, [accessQuote.idCotacaoSalesforce, successCreateMensalPs]);

  // Sempre que as parcelas mudarem por causa de uma edição do usuário
  // (hasChanges = true), salva automaticamente um rascunho no localStorage.
  React.useEffect(() => {
    if (isRestoringDraft.current) {
      isRestoringDraft.current = false;
      return;
    }

    if (!hasUserEditedRef.current) return;

    const boxesToPersist = boxes.filter((box) => !isBoxEmpty(box));

    if (boxesToPersist.length > 0) {
      saveDraftToLocalStorage(boxesToPersist);
    } else {
      clearLocalDraft();
    }
  }, [boxes, isBoxEmpty, saveDraftToLocalStorage, clearLocalDraft]);

  React.useEffect(() => {
    const newHeader = boxes.find((box) =>
      box.tipoFluxo?.toLowerCase()?.includes("escalo"),
    );

    setIsEscalonado(!!newHeader);
  }, [boxes]);

  React.useEffect(() => {
    const tempDiscount = discount.replace(/\D/g, "");

    const numericValue = Number(tempDiscount) / 100;

    setFormattedDiscount(numericValue);

    const formattedValue = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue);

    setDiscount(formattedValue);
  }, [discount]);

  React.useEffect(() => {
    // Se acabamos de restaurar um rascunho na montagem, ainda estamos no
    // mesmo ciclo de efeitos em que `boxes` continua vazio (o setBoxes do
    // efeito de restauração ainda não "pegou"). Sem essa guarda, este
    // efeito conclui erroneamente que não há parcelas pendentes e chama
    // clearLocalDraft(), apagando o rascunho do localStorage mesmo ele
    // tendo acabado de ser restaurado na tela.
    if (skipClearOnRestoreRef.current) {
      skipClearOnRestoreRef.current = false;
      return;
    }

    const hasLocalInstallments = boxes.some(
      (box) => !box.deletado && !box.idParcelamentoSalesforce,
    );

    const hasDeletedBackendInstallments = boxes.some(
      (box) => box.deletado && !!box.idParcelamentoSalesforce,
    );

    // Só limpa quando não existir nenhuma parcela nova
    // E também não existir nenhuma parcela do backend marcada para exclusão
    if (!hasLocalInstallments && !hasDeletedBackendInstallments) {
      clearLocalDraft();
      setShowUnsavedMessage(false);
      setHasChanges(false);
    }
  }, [boxes, clearLocalDraft]);

  return (
    <Stack display={"flex"} flexDirection={"column"}>
      {/* <MessageToast
        onOpen={error}
        message={messageError ? messageError : "Erro ao salvar parcelamento!"}
      /> */}

      {showUnsavedMessage && hasLocalDraft && (
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          bg={"#FFFAF0"}
          borderWidth={1}
          borderColor={"#FCE7D9"}
          borderRadius={"8px"}
          p={"12px 14px"}
          mb={"4px"}
        >
          <Text
            display={"flex"}
            gap={"10px"}
            fontSize={"12px"}
            color={"orange.700"}
          >
            <Box w={"fit-content"}>
              <IconInfo width="20" height="20" />
            </Box>{" "}
            Você tem alterações não salvas, clique em "Salvar parcelamento" para
            confirmar.
            {/* {draftSavedAt &&
              ` desde ${new Date(draftSavedAt).toLocaleTimeString("pt-BR")}`}
            ). Clique em "SALVAR PARCELAMENTO" para confirmar. */}
          </Text>
        </Box>
      )}

      {isEscalonado ? (
        <Box display={"flex"}>
          {titleHeaderTable.map(({ id, name }) => (
            <Box
              key={id}
              display={"flex"}
              justifyContent={"space-between"}
              textAlign={
                id === "#" || (id === "Ação" && isAssociative)
                  ? "initial"
                  : "center"
              }
              w={handleAdjustWidth(id)}
            >
              <Text fontSize={"10px"} color={theme.text}>
                {name}
              </Text>
            </Box>
          ))}
        </Box>
      ) : (
        <Box display={"flex"}>
          {titleHeaderTableNoProsoluto.map(({ id, name }) => (
            <Box
              key={id}
              display={"flex"}
              justifyContent={"space-between"}
              textAlign={
                id === "#" || (id === "Ação" && isAssociative)
                  ? "initial"
                  : "center"
              }
              w={handleAdjustWidth(id)}
            >
              <Text fontSize={"10px"} color={theme.text}>
                {name}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      <Box display={"flex"}>
        {loadingInstallment ? (
          <Box
            display={"flex"}
            flex={1}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Spinner />
          </Box>
        ) : (
          <Flex direction={"column"}>
            {!boxesEmpty() ? (
              <Box
                display={"flex"}
                bg={theme.backgroundGray}
                h={"40px"}
                borderRadius={"8px"}
                alignItems={"center"}
                pl={"8px"}
              >
                <Text fontSize={"11px"} fontWeight={"bold"}>
                  Adicione parcelas para criar uma condição de pagamento.
                </Text>
              </Box>
            ) : (
              <Flex direction={"column"}>
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems="center"
                  flexDirection={"column"}
                  rowGap={4}
                >
                  {boxes
                    .filter((box) => !box.deletado)
                    .map((box, index, filteredBoxes) => {
                      //habilitar edição
                      // const isLastBox = index === filteredBoxes.length - 1;
                      const canDelete =
                        box.serie === "Mensal PS" ||
                        !boxes.some((b) => b.serie === "Mensal PS");

                      return (
                        <Box key={box.id} display={"flex"} gap={"4px"}>
                          <Box display={"flex"} w="25%" alignItems={"center"}>
                            <Box
                              display={"flex"}
                              w={"40px"}
                              h={"40px"}
                              borderRadius={"8px"}
                              bg={theme.primary}
                              color={"white"}
                              alignItems={"center"}
                              justifyContent={"center"}
                              textAlign={"center"}
                            >
                              <Text fontSize={"15px"} fontWeight={"bold"}>
                                {index + 1 <= 9 ? `0${index + 1}` : index + 1}
                              </Text>
                            </Box>
                          </Box>

                          {/* {isAssociative ? (
                          <Box display={"none"} w="75%" alignItems={"center"}>
                            <Box
                              display={"flex"}
                              w="70%"
                              fontSize={"22px"}
                              gap={"6px"}
                            >
                              <BiBarcodeReader
                                color={theme.primary}
                                cursor={"pointer"}
                              />
                              <FaRegCreditCard
                                color={theme.primary}
                                cursor={"pointer"}
                              />
                              <MdOutlinePix
                                color={theme.primary}
                                cursor={"pointer"}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            display={"flex"}
                            w="75%"
                            alignItems={"center"}
                            justifyContent={"center"}
                          >
                            <Text
                              fontSize={"11px"}
                              fontWeight={"bold"}
                              textDecoration={"underline"}
                              color={theme.primary}
                              align={"center"}
                              cursor={"pointer"}
                            >
                              Link para download
                            </Text>
                          </Box>
                        )} */}

                          <Box display={"flex"} justifyContent={"center"}>
                            <InputSelectWithService
                              widht="20%"
                              title=""
                              label={box.serie || "Selecione uma opção"}
                              isSelected={box.serie}
                              options={dataSerie}
                              selectedOption={box.serie}
                              onClick={() => handleGetTypeInstallment()}
                              onChange={(e) =>
                                handleInputChange(
                                  box.id,
                                  "serie",
                                  e.target.value,
                                )
                              }
                              setId={setIdSerie}
                              loading={loadingSerie}
                              disabled={
                                // box.isDisabled ||
                                // !isLastBox ||
                                boxes.some((b) => b.serie === "Mensal PS") ||
                                box.serie === "Mensal PS" ||
                                isSendToApproval
                              }
                            />
                          </Box>
                          <Box display={"flex"} justifyContent={"center"}>
                            <InputText
                              label=""
                              name="quantity"
                              value={box.quantidade}
                              onChange={(e) =>
                                handleInputChange(
                                  box.id,
                                  "quantidade",
                                  e.target.value,
                                )
                              }
                              readOnly={
                                // box.isDisabled ||
                                // !isLastBox ||
                                boxes.some((b) => b.serie === "Mensal PS") ||
                                box.serie === "Mensal PS" ||
                                isSendToApproval
                              }
                            />
                          </Box>
                          <Box display={"flex"} justifyContent={"center"}>
                            <InputText
                              label=""
                              name="value"
                              value={box.valor}
                              onChange={(e) => {
                                const { formattedValue } =
                                  handleChangeMoneyDiscount(
                                    e,
                                    box.serie,
                                    bonusAdimplencia,
                                  );
                                handleInputChange(
                                  box.id,
                                  "valor",
                                  formattedValue,
                                );
                              }}
                              readOnly={
                                // box.isDisabled ||
                                // !isLastBox ||
                                boxes.some((b) => b.serie === "Mensal PS") ||
                                box.serie === "Mensal PS" ||
                                isSendToApproval
                              }
                            />
                          </Box>
                          <Box display={"flex"} justifyContent={"center"}>
                            <InputDate
                              min={getDateToDay()}
                              type="date"
                              name={"date"}
                              value={box.dataPagamento}
                              onChange={(e) => {
                                handleInputChange(
                                  box.id,
                                  "dataPagamento",
                                  e.target.value,
                                );
                              }}
                              readOnly={
                                // box.isDisabled ||
                                // !isLastBox ||
                                boxes.some((b) => b.serie === "Mensal PS") ||
                                box.serie === "Mensal PS" ||
                                isSendToApproval
                              }
                            />
                          </Box>

                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            textAlign={"center"}
                            justifyContent={"center"}
                            w="40%"
                          >
                            <Text
                              fontSize={"11px"}
                              fontWeight={"bold"}
                              w={"fit-content"}
                            >
                              {calcularPercentualParcela(box)}
                            </Text>
                          </Box>

                          {isEscalonado && (
                            <Box display={"flex"} justifyContent={"center"}>
                              <InputText
                                label=""
                                name="prosoluto"
                                value={box.prosoluto}
                                onChange={(e) =>
                                  handleInputChange(
                                    box.id,
                                    "prosoluto",
                                    e.target.value,
                                  )
                                }
                                readOnly={
                                  (box.serie !== "Mensal PS" && isEscalonado) ||
                                  isSendToApproval
                                }
                              />
                            </Box>
                          )}

                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            textAlign={"center"}
                          >
                            <Text fontSize={"11px"} fontWeight={"bold"}>
                              {valueTotalCondition(box)}{" "}
                            </Text>

                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"flex-end"}
                              textAlign={"center"}
                              w={"fit-content"}
                            >
                              <Box
                                display={"flex"}
                                w={"30px"}
                                h={"30px"}
                                borderWidth={"1px"}
                                borderColor={
                                  isSendToApproval ||
                                  disableEdit ||
                                  disabledStatusApprove ||
                                  !canDelete
                                    ? theme.disable
                                    : theme.error
                                }
                                borderRadius={"8px"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                textAlign={"center"}
                                cursor={
                                  isSendToApproval ||
                                  disableEdit ||
                                  disabledStatusApprove ||
                                  !canDelete
                                    ? "not-allowed"
                                    : "pointer"
                                }
                                onClick={() => {
                                  if (
                                    isSendToApproval ||
                                    disableEdit ||
                                    disabledStatusApprove ||
                                    !canDelete
                                  ) {
                                  } else {
                                    handleRemoveBox(box.id, box.serie);
                                  }
                                }}
                              >
                                <IoTrashOutline
                                  color={
                                    isSendToApproval ||
                                    disableEdit ||
                                    disabledStatusApprove ||
                                    !canDelete
                                      ? theme.disable
                                      : theme.error
                                  }
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Flex>
            )}
            <Flex w={"fit-content"} mt={"10px"}>
              <Box
                onClick={handleDisabledButtonAddBox}
                cursor={isAddPortionDisabled ? "not-allowed" : "pointer"}
              >
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  fontSize={"13px"}
                  fontWeight={"bold"}
                  color={isAddPortionDisabled ? theme.disable : theme.primary}
                  _hover={{ opacity: 0.8 }}
                >
                  <Box
                    width={"fit-content"}
                    fontSize={"20px"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <BiPlus />
                  </Box>
                  Adicionar parcela
                </Box>
              </Box>
            </Flex>
          </Flex>
        )}
      </Box>

      <Box
        display={"flex"}
        w={"full"}
        h={"1px"}
        bg={theme.disable}
        borderRadius={"8px"}
        mt={"4px"}
      />

      <Stack display={"flex"} flexDir={"row"} justifyContent={"space-between"}>
        {/*
        <Stack display={"flex"} flexDir={"row"} alignItems={"center"}>
           <Box display={"flex"} w={"200px"}>
            <Button
              title={
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Box
                    width={"fit-content"}
                    fontSize={"20px"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <BiPlus />
                  </Box>
                  Adicionar parcela
                </Box>
              }
              onClick={handleAddBox}
              disabled={
                !canAddMoreBoxes ||
                faseOp ||
                handleExistMensaPS() ||
                disableEdit ||
                disabledStatusApprove ||
                (disabledIncome && isAssociative)
              }
            />
          </Box> 
        </Stack>
          */}

        <Stack display={"flex"} flexDir={"row"}>
          <Box display={"flex"} w={{ base: "120px", "2xl": "170px" }}>
            <Box display={"flex"} flexDir={"column"}>
              <Box
                fontSize={{ base: "10px", "2xl": "14px" }}
                color={theme.text}
                display={"flex"}
                flexDir={"column"}
                gap={{ base: "", "2xl": "2px" }}
              >
                <Text>Valor da unidade: </Text>
                <Text fontWeight={"bold"} w={"fit-content"}>
                  {data?.valorTotalUnidade
                    ? formatMoney(data?.valorTotalUnidade)
                    : "R$ 0,00"}
                </Text>
              </Box>
              <Box
                fontSize={{ base: "10px", "2xl": "14px" }}
                color={theme.text}
                display={"flex"}
                flexDir={"column"}
                gap={{ base: "", "2xl": "2px" }}
              >
                <Text>Valor final com desconto: </Text>
                <Text
                  fontWeight={"bold"}
                  w={"fit-content"}
                  color={theme.primary}
                >
                  {data?.valorTotalUnidade
                    ? handleValueDiscount(
                        data?.valorTotalUnidade,
                        formattedDiscount,
                      )
                    : "R$ 0,00"}
                </Text>
              </Box>
            </Box>
          </Box>

          <Box
            display={"flex"}
            w={"1px"}
            h={"full"}
            bg={theme.disable}
            borderRadius={"8px"}
          />

          <Box display={"flex"} w={{ base: "120px", "2xl": "140px" }}>
            <Box display={"flex"} flexDir={"column"}>
              <Box
                fontSize={{ base: "10px", "2xl": "14px" }}
                color={theme.text}
                display={"flex"}
                flexDir={"column"}
                gap={{ base: "", "2xl": "2px" }}
              >
                <Text color={theme.text} display={"flex"}>
                  Soma do percentual:
                </Text>
                <Text fontWeight={"bold"} w={"fit-content"}>
                  {somaPercentuais.toFixed(2)}%
                </Text>
              </Box>

              <Box
                fontSize={{ base: "10px", "2xl": "14px" }}
                color={theme.text}
                display={"flex"}
                flexDir={"column"}
                gap={{ base: "", "2xl": "2px" }}
              >
                <Text>Saldo à distribuir: </Text>
                <Text
                  fontWeight={"bold"}
                  w={"fit-content"}
                  color={theme.primary}
                >
                  {formatMoney(saldoADistribuir)}
                </Text>
              </Box>
            </Box>
          </Box>

          <Box
            display={"flex"}
            w={"1px"}
            h={"full"}
            bg={theme.disable}
            borderRadius={"8px"}
          />

          <Box display={"flex"} w={{ base: "120px", "2xl": "230px" }}>
            <Box display={"flex"} flexDir={"column"}>
              <Text
                fontSize={{ base: "10px", "2xl": "14px" }}
                color={theme.text}
                display={"flex"}
                gap={{ base: "", "2xl": "4px" }}
              >
                Soma do valor total da condição:
              </Text>
              <Text
                fontSize={{ base: "12px", "2xl": "14px" }}
                fontWeight={"bold"}
                w={"fit-content"}
              >
                {sumValoresTotal()}
              </Text>
            </Box>
          </Box>
        </Stack>

        <Box
          display={"flex"}
          w={hasLocalDraft ? "620px" : "220px"}
          gap={hasLocalDraft ? "7px" : undefined}
        >
          {hasLocalDraft && (
            <Button
              title="Descartar alterações"
              colorInverter
              onClick={handleDiscardLocalDraft}
            />
          )}

          <Button
            title={
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"4px"}
              >
                {loadingCreateInstallment ? (
                  <Center>
                    <Spinner />
                  </Center>
                ) : (
                  <>
                    <Box
                      width={"fit-content"}
                      fontSize={"20px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <MdOutlineSave />
                    </Box>
                    Salvar parcelamento
                  </>
                )}
              </Box>
            }
            onClick={handleCreatePortion}
            disabled={isSalvarParcelamentoDisabled}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default TablePortion;
